import React, { useCallback, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { documentApi, peopleApi } from '../services/api';
import { useToast } from '../hooks/useToast';
import { useSwal } from '../hooks/useSwal';
import { PageHeader } from '../components/PageHeader';
import { NimbusTable } from '../components/NimbusTable';
import { SortableTh } from '../components/SortableTh';
import { useSortableData } from '../hooks/useSortableData';
import { usePagination } from '../hooks/usePagination';
import { Pagination } from '../components/Pagination';
import { NimbusModal, NimbusModalContent } from '../components/NimbusModal';
import { useAuth } from '../hooks/useAuth';

type Doc = {
  documentId: string;
  title?: string;
  documentType?: string;
  versionLabel?: string;
  status?: 'draft' | 'published' | 'archived' | string;
  author?: string;
  lastEditedAt?: string;
  // AI-drafted content awaiting review before it can be merged into this document - see
  // DocumentDetail.tsx's Pending Changes card. Only the count is used here.
  pendingChanges?: Array<{ status: 'pending' | 'accepted' | 'rejected' }>;
  // 'register' + a real registerId for a normal, register-parented document; some other
  // entityType (e.g. 'ai_use_case', 'processing_activity') for one that's linked directly to
  // the record that created it instead - a DPIA is always this second shape by design.
  entityType?: string;
  entityId?: string;
};

/** Where a non-register-parented document links back to - only the types the app actually
 *  creates documents for need an entry; anything else falls back to a plain, unlinked label. */
const ENTITY_LINK_LABELS: Record<string, string> = {
  ai_use_case: 'AI Use Case',
  processing_activity: 'Processing Activity',
};
const ENTITY_LINK_ROUTES: Record<string, string> = {
  ai_use_case: '/ai-use-cases',
  processing_activity: '/processing-activities',
};

const ALL_DOCUMENTS = '__all__';

type SortCol = 'title' | 'documentType' | 'status' | 'versionLabel' | 'owner' | 'lastEditedAt';

const formatDate = (iso?: string) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
};

const DOC_TYPE_ICONS: Record<string, string> = {
  Policy: 'ri-shield-check-line',
  Procedure: 'ri-list-check-2',
  Process: 'ri-flow-chart',
  Standard: 'ri-award-line',
  Guideline: 'ri-lightbulb-line',
  Plan: 'ri-map-2-line',
  Report: 'ri-bar-chart-box-line',
  Manual: 'ri-book-open-line',
};

const DOC_TYPE_COLOURS: Record<string, string> = {
  Policy: 'primary',
  Procedure: 'info',
  Process: 'warning',
  Standard: 'success',
  Guideline: 'secondary',
  Plan: 'danger',
  Report: 'dark',
  Manual: 'secondary',
};

export const DocumentsList: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialRegisterId = searchParams.get('registerId') || ALL_DOCUMENTS;

  const queryClient = useQueryClient();
  const toast = useToast();
  const swal = useSwal();
  const { user } = useAuth();
  const plan = user?.plan ?? 'basic';
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [registerId, setRegisterId] = useState<string>(initialRegisterId);
  // The sentinel is a real, truthy value (so the documents query stays enabled) but must never
  // be treated as an actual register id anywhere - passing '__all__' through to registerId=
  // on a link, or worse into a save payload, would corrupt whatever reads it back as if it
  // were a real register.
  const hasRegister = !!registerId && registerId !== ALL_DOCUMENTS;
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [publishTarget, setPublishTarget] = useState<Doc | null>(null);
  const [changeReasonInput, setChangeReasonInput] = useState<string>('');

  const { data: registers = [], isLoading: isLoadingRegisters } = useQuery<any[]>({
    queryKey: ['documentRegisters'],
    queryFn: documentApi.getRegisters,
  });
  const { data: people = [] } = useQuery<any[]>({ queryKey: ['people'], queryFn: peopleApi.getAll });
  const personName = useCallback(
    (personId: string) => people.find((p) => p.personId === personId)?.personName || personId,
    [people],
  );

  const {
    data: documents = [],
    isLoading: isLoadingDocs,
    error: docsError,
  } = useQuery<Doc[]>({
    queryKey: ['documents', registerId],
    queryFn: () => (registerId === ALL_DOCUMENTS ? documentApi.listAll() : documentApi.listByRegister(registerId)),
    enabled: !!registerId,
  });
  const registerNameById = useMemo(() => {
    const map = new Map<string, string>();
    registers.forEach((r: any) => map.set(r.registerId, r.name || r.registerName || r.registerId));
    return map;
  }, [registers]);

  const publishMutation = useMutation({
    mutationFn: async ({ documentId, versionType, changeReason }: { documentId: string; versionType: 'major' | 'minor'; changeReason: string }) =>
      documentApi.publish(documentId, versionType, changeReason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', registerId] });
      toast.success('Document published.');
      setPublishTarget(null);
      setChangeReasonInput('');
    },
    onError: (e: Error) => toast.error(`Failed to publish: ${e.message}`),
  });

  const deleteMutation = useMutation({
    mutationFn: async (documentId: string) => documentApi.delete(documentId, true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', registerId] });
      toast.success('Document deleted.');
    },
    onError: async (e: Error) => toast.error(`Failed to delete: ${e.message}`),
  });

  const searched = useMemo(() => {
    let items = documents;

    if (statusFilter) {
      items = items.filter((d) => (d.status || 'draft') === statusFilter);
    }

    if (typeFilter) {
      items = items.filter((d) => (d.documentType || '') === typeFilter);
    }

    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      items = items.filter((d) => {
        const title = (d.title || '').toLowerCase();
        const id = (d.documentId || '').toLowerCase();
        const author = (d.author ? personName(d.author) : '').toLowerCase();
        return title.includes(term) || id.includes(term) || author.includes(term);
      });
    }

    return items;
  }, [documents, statusFilter, typeFilter, searchTerm, personName]);

  const { sorted: filtered, field: sortField, dir: sortDir, toggle: toggleSort } = useSortableData<Doc, SortCol>(
    searched,
    (d, col) => {
      switch (col) {
        case 'title':        return d.title || '';
        case 'documentType': return d.documentType || '';
        case 'status':       return d.status || 'draft';
        case 'versionLabel': return d.versionLabel || '0.0';
        case 'owner':        return d.author ? personName(d.author) : '';
        case 'lastEditedAt': return d.lastEditedAt || '';
      }
    },
    'title',
  );
  const { pageItems, page, setPage, totalPages, pageSize, totalItems } = usePagination(filtered, 25);

  const typeOptions = useMemo(() => {
    const set = new Set<string>();
    documents.forEach((d) => {
      if (d.documentType) set.add(d.documentType);
    });
    return Array.from(set).sort();
  }, [documents]);

  const selectedRegister = registers.find((r: any) => r.registerId === registerId);

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-file-text-line"
        title="Documents"
        colour="info"
        actions={
          <>
            <Link
              className="np-btn np-btn-primary"
              to={hasRegister ? `/documents/new?registerId=${encodeURIComponent(registerId)}` : '/documents/new'}
            >
              <i className="ri-add-line"></i>New Document
            </Link>
            {hasRegister && (
              <button
                className="np-btn np-btn-ghost"
                onClick={() => plan === 'advanced'
                  ? (window.location.href = `/policy-templates?registerId=${encodeURIComponent(registerId)}`)
                  : setShowUpgradeModal(true)
                }
              >
                <i className="ri-sparkling-2-line"></i>AI Policy Templates
              </button>
            )}
            <Link className="np-btn np-btn-ghost" to="/document-registers">
              <i className="ri-folder-settings-line"></i>Registers
            </Link>
          </>
        }
      />

      {/* Register + Filter Bar */}
      <div className="np-card">
        <div className="np-card-body">
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-4">
              <div className="np-field np-mb-0">
                <label><i className="ri-folder-2-line" style={{ color: 'var(--bs-primary)' }}></i> Register</label>
                <select
                  className="np-select"
                  value={registerId}
                  onChange={(e) => setRegisterId(e.target.value)}
                  disabled={isLoadingRegisters}
                >
                  <option value={ALL_DOCUMENTS}>All Documents</option>
                  {registers.map((r: any) => (
                    <option key={r.registerId} value={r.registerId}>
                      {r.name || r.registerName || r.registerId}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="col-6 col-md-2">
              <div className="np-field np-mb-0">
                <label>Status</label>
                <select className="np-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="">All statuses</option>
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>

            <div className="col-6 col-md-2">
              <div className="np-field np-mb-0">
                <label>Type</label>
                <select className="np-select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                  <option value="">All types</option>
                  {typeOptions.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="col-12 col-md-4">
              <div className="np-field np-mb-0">
                <label>Search</label>
                <div className="input-group">
                  <span className="input-group-text"><i className="ri-search-line"></i></span>
                  <input
                    className="np-input"
                    placeholder="Title, ID, or owner…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <button className="input-group-text" type="button" onClick={() => setSearchTerm('')} style={{ cursor: 'pointer' }}>
                      <i className="ri-close-line"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Register banner */}
      {selectedRegister && (
        <div className="d-flex align-items-center gap-3 mb-3">
          <div>
            <h6 className="mb-0" style={{ fontWeight: 600 }}>
              <i className="ri-folder-open-line me-2" style={{ color: 'var(--bs-primary)' }}></i>
              {selectedRegister.name || selectedRegister.registerName}
            </h6>
            {selectedRegister.description && (
              <small className="np-hint">{selectedRegister.description}</small>
            )}
          </div>
          <span className="np-chip np-tone-primary np-tabular ms-auto">
            {filtered.length} document{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>
      )}

      {/* Content area */}
      {!registerId ? (
        <div className="np-card">
          <div className="np-card-body py-5 text-center">
            <i className="ri-folder-2-line" style={{ fontSize: '3rem', color: 'var(--bs-secondary-color)' }}></i>
            <h6 className="mt-3 np-sub">Select a register above to view documents</h6>
            <Link to="/document-registers" className="np-btn np-btn-ghost mt-2">
              <i className="ri-folder-settings-line"></i>Manage Registers
            </Link>
          </div>
        </div>
      ) : isLoadingDocs ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : docsError ? (
        <div className="np-banner np-tone-danger"><i className="ri-error-warning-line"></i>Failed to load documents.</div>
      ) : filtered.length === 0 ? (
        <div className="np-card">
          <div className="np-card-body py-5 text-center">
            <i className="ri-file-text-line" style={{ fontSize: '3rem', color: 'var(--bs-secondary-color)' }}></i>
            <h6 className="mt-3 np-sub">
              {documents.length === 0
                ? (hasRegister ? 'No documents in this register yet' : 'No documents yet')
                : 'No documents match your filters'}
            </h6>
            {hasRegister && (
              <div className="d-flex gap-2 justify-content-center mt-3">
                <Link
                  to={`/documents/new?registerId=${encodeURIComponent(registerId)}`}
                  className="np-btn np-btn-primary np-btn-sm"
                >
                  <i className="ri-add-line"></i>New Document
                </Link>
                <button
                  className="np-btn np-btn-ghost np-btn-sm"
                  onClick={() => plan === 'advanced'
                    ? (window.location.href = `/policy-templates?registerId=${encodeURIComponent(registerId)}`)
                    : setShowUpgradeModal(true)
                  }
                >
                  <i className="ri-sparkling-2-line"></i>Use AI Template
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
        <NimbusTable
          thead={
            <tr>
              <SortableTh field="title" active={sortField === 'title'} dir={sortDir} onSort={toggleSort}>Title</SortableTh>
              {!hasRegister && <th style={{ width: '190px' }}>Register / Link</th>}
              <SortableTh field="documentType" active={sortField === 'documentType'} dir={sortDir} onSort={toggleSort} width="140px">Type</SortableTh>
              <SortableTh field="status" active={sortField === 'status'} dir={sortDir} onSort={toggleSort} width="110px">Status</SortableTh>
              <SortableTh field="versionLabel" active={sortField === 'versionLabel'} dir={sortDir} onSort={toggleSort} width="90px">Version</SortableTh>
              <SortableTh field="owner" active={sortField === 'owner'} dir={sortDir} onSort={toggleSort} width="160px">Owner</SortableTh>
              <SortableTh field="lastEditedAt" active={sortField === 'lastEditedAt'} dir={sortDir} onSort={toggleSort} width="140px">Last edited</SortableTh>
              <th style={{ width: '130px' }}></th>
            </tr>
          }
          isEmpty={filtered.length === 0}
          emptyState={
            <div className="text-center py-5">
              <i className="ri-inbox-line ri-48px d-block mb-2" style={{ color: 'var(--bs-secondary-color)' }}></i>
              <p className="np-sub mb-0">No documents match your filters.</p>
            </div>
          }
        >
          {pageItems.map((d) => {
            const status = d.status || 'draft';
            const docType = d.documentType || 'Other';
            const icon = DOC_TYPE_ICONS[docType] || 'ri-file-text-line';
            const colour = DOC_TYPE_COLOURS[docType] || 'secondary';
            const statusTone = status === 'published'
              ? 'success'
              : status === 'archived'
                ? 'warning'
                : 'neutral';
            const docTone = colour === 'secondary' || colour === 'dark' ? 'neutral' : colour;

            return (
              <tr key={d.documentId}>
                <td>
                  <div className="d-flex align-items-center gap-2">
                    <span className={`np-icon-badge np-icon-badge-sm np-tone-${docTone} flex-shrink-0`}>
                      <i className={icon}></i>
                    </span>
                    <span className="fw-medium text-truncate" title={d.title || 'Untitled Document'}>
                      {d.title || 'Untitled Document'}
                    </span>
                    {(() => {
                      const pendingCount = (d.pendingChanges || []).filter((c) => c.status === 'pending').length;
                      return pendingCount > 0 ? (
                        <span className="np-chip np-tone-warning flex-shrink-0" title={`${pendingCount} AI-drafted change${pendingCount !== 1 ? 's' : ''} awaiting review`}>
                          <i className="ri-git-pull-request-line"></i>{pendingCount}
                        </span>
                      ) : null;
                    })()}
                  </div>
                </td>
                {!hasRegister && (
                  <td>
                    {d.entityType === 'register' && d.entityId ? (
                      <Link to={`/documents?registerId=${encodeURIComponent(d.entityId)}`} className="np-chip np-tone-neutral">
                        <i className="ri-folder-2-line"></i>{registerNameById.get(d.entityId) || d.entityId}
                      </Link>
                    ) : d.entityType && ENTITY_LINK_LABELS[d.entityType] && d.entityId ? (
                      <Link to={`${ENTITY_LINK_ROUTES[d.entityType]}/${encodeURIComponent(d.entityId)}`} className="np-chip np-tone-info">
                        <i className="ri-link"></i>{ENTITY_LINK_LABELS[d.entityType]}: {d.entityId}
                      </Link>
                    ) : (
                      <span className="np-sub fst-italic">No register</span>
                    )}
                  </td>
                )}
                <td>{docType !== 'Other' ? <span className={`np-chip np-tone-${docTone}`}>{docType}</span> : <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>}</td>
                <td><span className={`np-chip np-tone-${statusTone}`} style={{ textTransform: 'capitalize' }}>{status}</span></td>
                <td className="np-tabular">v{d.versionLabel || '0.0'}</td>
                <td>{d.author ? personName(d.author) : <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>}</td>
                <td className="np-tabular">{formatDate(d.lastEditedAt)}</td>
                <td>
                  <div className="d-flex gap-1 justify-content-end">
                    <Link
                      className="np-icon-btn"
                      to={hasRegister
                        ? `/documents/${encodeURIComponent(d.documentId)}?registerId=${encodeURIComponent(registerId)}`
                        : `/documents/${encodeURIComponent(d.documentId)}`}
                      title={status === 'published' ? 'View' : 'Edit'}
                    >
                      <i className={status === 'published' ? 'ri-eye-line' : 'ri-edit-line'}></i>
                    </Link>
                    {status === 'draft' && (
                      <button
                        type="button"
                        className="np-icon-btn np-icon-btn-success"
                        title="Publish"
                        onClick={() => { setPublishTarget(d); setChangeReasonInput(''); }}
                        disabled={publishMutation.isPending}
                      >
                        <i className="ri-checkbox-circle-line"></i>
                      </button>
                    )}
                    <button
                      type="button"
                      className="np-icon-btn np-icon-btn-danger"
                      title="Delete"
                      onClick={async () => {
                        const ok = await swal.confirmDelete(d.title || 'Untitled');
                        if (!ok) return;
                        deleteMutation.mutate(d.documentId);
                      }}
                      disabled={deleteMutation.isPending}
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </NimbusTable>
        {pageItems.length > 0 && (
          <Pagination page={page} totalPages={totalPages} pageSize={pageSize} totalItems={totalItems} onPageChange={setPage} />
        )}
        </>
      )}

      {/* AI upgrade modal */}
      {showUpgradeModal && (
        <NimbusModal centered>
          <NimbusModalContent
            title={<><i className="ri-vip-crown-line me-2 text-warning"></i>Advanced Plan Required</>}
            onClose={() => setShowUpgradeModal(false)}
            footer={
              <>
                <button className="np-btn np-btn-ghost" onClick={() => setShowUpgradeModal(false)}>Close</button>
                <a className="np-btn np-btn-primary" href="/settings">Upgrade Plan</a>
              </>
            }
          >
            <div className="py-4 text-center">
              <i className="ri-sparkling-2-line ri-3x text-warning mb-3 d-block"></i>
              <p className="mb-2">AI policy generation is available on the <strong>Advanced plan</strong> only.</p>
              <p className="np-hint mb-0">Upgrade your subscription to unlock AI-assisted policy drafting.</p>
            </div>
          </NimbusModalContent>
        </NimbusModal>
      )}

      {/* Publish modal — publishing needs a changeReason (enforced server-side), so this
          collects one rather than firing a bare confirm() that always 400s. */}
      {publishTarget && (
        <NimbusModal centered>
          <NimbusModalContent
            title={<><i className="ri-checkbox-circle-line me-2 text-primary"></i>Publish Document</>}
            onClose={() => { setPublishTarget(null); setChangeReasonInput(''); }}
            footer={
              <>
                <button type="button" className="np-btn np-btn-ghost" onClick={() => { setPublishTarget(null); setChangeReasonInput(''); }}>Cancel</button>
                <button
                  type="button"
                  className="np-btn np-btn-ghost"
                  onClick={() => publishMutation.mutate({ documentId: publishTarget.documentId, versionType: 'major', changeReason: changeReasonInput })}
                  disabled={publishMutation.isPending || !changeReasonInput.trim()}
                >
                  Publish as Major
                </button>
                <button
                  type="button"
                  className="np-btn np-btn-primary"
                  onClick={() => publishMutation.mutate({ documentId: publishTarget.documentId, versionType: 'minor', changeReason: changeReasonInput })}
                  disabled={publishMutation.isPending || !changeReasonInput.trim()}
                >
                  {publishMutation.isPending ? 'Publishing…' : 'Publish as Minor'}
                </button>
              </>
            }
          >
            <p className="np-sub">Publish "{publishTarget.title || 'Untitled Document'}"?</p>
            <div className="np-field np-mb-0">
              <label>Reason for this change <span className="text-danger">*</span></label>
              <textarea
                className="np-textarea"
                rows={2}
                value={changeReasonInput}
                onChange={(e) => setChangeReasonInput(e.target.value)}
                placeholder="What changed and why - this becomes part of the document's permanent change log."
              />
            </div>
          </NimbusModalContent>
        </NimbusModal>
      )}
    </div>
  );
};
